import { Box, TextInput } from "@mantine/core";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { TFunction } from "react-i18next";

import Button from "components/Button";
import Flex from "components/Flex";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { claimHandlingDecisionAtom } from "atoms/claimHandlingDecision";
import { damagePhotosAtom } from "atoms/damagePhotos";
import { registrationAtom } from "atoms/registrationPlate";
import { bodyShopAtom } from "atoms/selectBodyShop";
import { serverSessionAtom } from "atoms/serverSession";
import { vehiclePhotosAtom } from "atoms/vehiclePhotos";
import { vehicleTypeAtom } from "atoms/vehicleType";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { VehicleType } from "types/DigitalJourneySession";

export const vehicleTypeOptions = (t: TFunction) => [
  {
    label: t("digital_journey.select_vehicle.vehicle_type_car"),
    value: VehicleType.CAR,
  },
  {
    label: t("digital_journey.select_vehicle.vehicle_type_moto"),
    value: VehicleType.MOTORCYCLE,
  },
];

export const DJVehicleTypeSelectionPage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const [registration, setRegistration] = useAtom(registrationAtom);
  const [vehicleType, setVehicleType] = useAtom(vehicleTypeAtom);
  const serverSession = useAtomValue(serverSessionAtom);
  const setVehiclePhotos = useSetAtom(vehiclePhotosAtom);
  const setDamagePhotos = useSetAtom(damagePhotosAtom);
  const setSelectedBodyShop = useSetAtom(bodyShopAtom);
  const setClaimHandlingDecision = useSetAtom(claimHandlingDecisionAtom);

  const isDisabled = !registration || !vehicleType;

  const onContinue = () => {
    trackEvent({ name: "Digital Journey Select Vehicle - continue" });
    if (vehicleType === VehicleType.MOTORCYCLE) {
      setSelectedBodyShop(null);
      setDamagePhotos([]);
      setVehiclePhotos([]);
      setClaimHandlingDecision(null);
    }
    if (!isDisabled) onNext();
  };

  useEffect(() => {
    if (!registration && serverSession && serverSession.digitalJourney && serverSession.digitalJourney.licensePlatePh) {
      setRegistration(serverSession.digitalJourney.licensePlatePh);
    }
  }, []);

  return (
    <PageLayout contentSpacing="space-between">
      <Flex px={24} py={24} gap={16} flex={1} mb={16}>
        <Text as="h1" sizeVariant="xl" weight={600}>
          {t("digital_journey.select_vehicle.title")}
        </Text>
        <Text>{t("digital_journey.select_vehicle.description")}</Text>
        <TextInput
          name="registration"
          value={registration}
          label={<Text sizeVariant="md">{t("digital_journey.select_vehicle.reg_plate_label")}</Text>}
          onChange={(e) => setRegistration(e.target.value)}
          maxLength={8}
        />
        <Box>
          <Text weight={500} sizeVariant="md" mb={4}>
            {t("digital_journey.select_vehicle.vehicle_type_label")}
          </Text>
          <Flex gap={16} direction="row" role="radiogroup" aria-labelledby="vehicleType">
            {vehicleTypeOptions(t).map((option) => (
              <Button
                aria-labelledby={`vehicleType-${option.label}-label`}
                aria-checked={vehicleType === option.value}
                role="radio"
                key={option.label}
                onClick={() => setVehicleType(option.value)}
                typeVariant={vehicleType === option.value ? "primary" : "secondary"}
              >
                <label id={`vehicleType-${option.label}-label`}>{option.label}</label>
              </Button>
            ))}
          </Flex>
        </Box>
      </Flex>
      <Flex px={24} py={24} gap={16}>
        <Button disabled={isDisabled} onClick={onContinue}>
          {continueCTA}
        </Button>
      </Flex>
    </PageLayout>
  );
};
